// "Nearby parking options" — honest wording only: live SpotDrops are confirmed
// hand-overs, everything else is an option, never a guaranteed space.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Navigation, Zap, Search } from "lucide-react";
import { fetchNearbyParking } from "../lib/parkingPlaces";
import {
  NEARBY_RADII,
  NEARBY_PREFERENCES,
  KIND_META,
  accessibleLabel,
  accessibleSource,
  loadNearbyPrefs,
  saveNearbyPrefs,
} from "../lib/mapLayers";
import { navTarget } from "../lib/journey";
import { placeIconHtml } from "../lib/placeIcons";
import { track } from "../lib/analytics";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";

const haversine = (a, b) => {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const fmtDist = (m) => (m < 950 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`);

const Chip = ({ active, children, ...rest }) => (
  <button
    type="button"
    className={`h-8 px-3 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${
      active ? "bg-[#ccff00] text-[#0a0a0c]" : "bg-white/5 text-zinc-400 border border-white/10"
    }`}
    {...rest}
  >
    {children}
  </button>
);

function NearbyParkingSheet({ open, onClose, position, liveSpots, onFocusSpot }) {
  const dismissProps = useBackdropDismiss(open, onClose);
  const [prefs, setPrefs] = React.useState(loadNearbyPrefs);
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // The search anchor is captured once when the sheet opens. A phone's
  // `watchPosition` fires constantly, and re-anchoring on every GPS tick used to
  // refetch and collapse the list under the user's finger.
  const [anchor, setAnchor] = React.useState(null);
  React.useEffect(() => {
    if (!open) {
      setAnchor(null);
      return;
    }
    setAnchor((a) => a || (position ? { lat: position.lat, lng: position.lng } : null));
  }, [open, position]);

  const load = React.useCallback(async () => {
    if (!anchor) return;
    setLoading(true);
    try {
      const res = await fetchNearbyParking({
        lat: anchor.lat,
        lng: anchor.lng,
        radius_m: prefs.radius,
        preference: prefs.preference,
      });
      setOptions(res);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [anchor, prefs.radius, prefs.preference]);

  React.useEffect(() => {
    if (open) load();
  }, [open, load]);

  const update = (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    saveNearbyPrefs(next);
  };

  // Live SpotDrop hand-overs always come first, then free, then paid/public.
  const live = React.useMemo(() => {
    if (!anchor || prefs.preference === "paid") return [];
    return (liveSpots || [])
      .filter((s) => !s.claimed)
      .map((s) => ({ ...s, distance_m: haversine(anchor, s) }))
      .filter((s) => s.distance_m <= prefs.radius)
      .sort((a, b) => a.distance_m - b.distance_m)
      .slice(0, 4);
  }, [liveSpots, anchor, prefs.radius, prefs.preference]);

  // "Any parking" is distance-first: an unknown fee is not a quality signal, so a
  // closer generic option must not sit below a further Free/Paid one. The dedicated
  // Free and Paid filters keep their type-first order.
  const ordered = React.useMemo(() => {
    const rank = { free_parking: 0, paid_parking: 1, car_park: 2, motorcycle_parking: 3 };
    const byKind = (a, b) => (rank[a.kind] ?? 9) - (rank[b.kind] ?? 9);
    return [...options].sort((a, b) =>
      prefs.preference === "any"
        ? a.distance_m - b.distance_m || byKind(a, b)
        : byKind(a, b) || a.distance_m - b.distance_m
    );
  }, [options, prefs.preference]);

  const total = live.length + ordered.length;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-black/60"
            {...dismissProps}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-[2001] max-h-[80vh] overflow-y-auto bg-[#141416] border-t border-white/10 rounded-t-3xl p-4 pb-7"
            data-testid="nearby-parking-sheet"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                  Best options nearby
                </div>
                <div className="font-heading text-lg font-semibold text-white mt-0.5">
                  Nearby parking options
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
                data-testid="nearby-parking-close"
                aria-label="Close nearby parking"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
              {NEARBY_PREFERENCES.map((p) => (
                <Chip
                  key={p.key}
                  active={prefs.preference === p.key}
                  onClick={() => update({ preference: p.key })}
                  data-testid={`nearby-pref-${p.key}`}
                >
                  {p.label}
                </Chip>
              ))}
            </div>
            <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
              {NEARBY_RADII.map((r) => (
                <Chip
                  key={r.key}
                  active={prefs.radius === r.key}
                  onClick={() => update({ radius: r.key })}
                  data-testid={`nearby-radius-${r.key}`}
                >
                  {r.label}
                </Chip>
              ))}
            </div>

            <div className="mt-3 space-y-1.5 min-h-[240px]" data-testid="nearby-parking-list">
              {loading && !total ? (
                <div className="py-8 flex items-center justify-center text-zinc-500 text-[13px]">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Looking around you…
                </div>
              ) : !anchor ? (
                <div className="py-8 text-center text-[13px] text-zinc-400" data-testid="nearby-no-location">
                  Turn on location to see parking options around you.
                </div>
              ) : total === 0 ? (
                <div className="py-8 text-center" data-testid="nearby-parking-empty">
                  <Search className="w-5 h-5 mx-auto mb-2 text-zinc-600" />
                  <div className="text-[13px] text-zinc-300">No parking options within this distance.</div>
                  <div className="text-[12px] text-zinc-500 mt-1">Try a wider radius or "Any parking".</div>
                </div>
              ) : (
                <>
                  {live.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        track("place_clicked", { type: "live_spot" });
                        onFocusSpot?.(s);
                        onClose();
                      }}
                      className="w-full text-left p-3 rounded-xl bg-[#ccff00]/[0.07] border border-[#ccff00]/30 flex items-center gap-3"
                      data-testid={`nearby-live-${s.id}`}
                    >
                      <span className="w-8 h-8 rounded-lg bg-[#ccff00] flex items-center justify-center shrink-0">
                        <Zap className="w-4 h-4 text-[#0a0a0c]" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-semibold text-white">
                          Live SpotDrop · driver leaving
                        </span>
                        <span className="block text-[11px] text-[#ccff00]/80">
                          Confirmed hand-over · {fmtDist(s.distance_m)} away
                        </span>
                      </span>
                    </button>
                  ))}
                  {ordered.map((p) => (
                    <div
                      key={p.id}
                      className="p-3 rounded-xl bg-white/[0.03] border border-white/10 flex items-center gap-3"
                      data-testid={`nearby-option-${p.id}`}
                    >
                      <span
                        className="shrink-0 flex items-center justify-center w-8"
                        dangerouslySetInnerHTML={{ __html: placeIconHtml(p.kind) }}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] text-white truncate">
                          {p.name || (KIND_META[p.kind]?.label ?? "Parking")}
                        </span>
                        <span className="block text-[11px] text-zinc-500">
                          {p.rule?.label || KIND_META[p.kind]?.label} · {fmtDist(p.distance_m)} away
                        </span>
                        {p.accessible?.has_bays && (
                          <span
                            className="block text-[11px] text-blue-300"
                            data-testid={`nearby-accessible-${p.id}`}
                          >
                            ♿ {accessibleLabel(p.accessible)} · {accessibleSource(p.accessible)}
                          </span>
                        )}
                      </span>
                      <a
                        href={(() => {
                          const t = navTarget(p, anchor);
                          return `https://www.google.com/maps/dir/?api=1&destination=${t.lat},${t.lng}`;
                        })()}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => track("place_clicked", { type: p.kind })}
                        className="shrink-0 h-8 px-3 rounded-full bg-white/5 border border-white/10 text-[12px] text-zinc-100 inline-flex items-center gap-1.5"
                        data-testid={`nearby-directions-${p.id}`}
                      >
                        <Navigation className="w-3.5 h-3.5 text-[#ccff00]" /> Go
                      </a>
                    </div>
                  ))}
                </>
              )}
            </div>

            <p className="mt-3 text-[11px] text-zinc-600 leading-relaxed">
              Only live SpotDrops are confirmed by a driver. Everything else is a known parking area —
              spaces are not guaranteed, always check the signs.
            </p>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Memoised: GPS ticks and map polling must not re-render the open sheet.
export default React.memo(NearbyParkingSheet);
